import React, { useRef, useEffect, useState } from 'react';
import './WaveformPlayer.css';

const WaveformPlayer = React.forwardRef(({
  src,
  isCurrentlyPlaying,
  onPlay,
  onPause,
  onEnded,
  onTimeUpdate,
  className = "",
  disabled = false,
  loop = false
}, ref) => {
  const audioRef = useRef(null);
  const canvasRef = useRef(null);

  // Expose audioRef through the forwarded ref
  React.useImperativeHandle(ref, () => ({
    audioRef: audioRef,
    getCurrentTime: () => audioRef.current?.currentTime || 0,
    getDuration: () => audioRef.current?.duration || 0
  }));
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [waveformData, setWaveformData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hoverTime, setHoverTime] = useState(-1);

    // Generate waveform data from audio
  useEffect(() => {
    if (!src) return;

    const generateWaveform = async () => {
      setIsLoading(true);
      console.log('🔧 Starting waveform generation for:', src);

      try {
        // Create audio context
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        console.log('🔧 Audio context created');

        // Fetch audio file
        const response = await fetch(src, {
          mode: 'cors',
          credentials: 'omit'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        console.log('🔧 Audio file fetched successfully');
        const arrayBuffer = await response.arrayBuffer();
        console.log('🔧 Array buffer created, size:', arrayBuffer.byteLength);

        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        console.log('🔧 Audio decoded, duration:', audioBuffer.duration, 'channels:', audioBuffer.numberOfChannels);

        const channelData = audioBuffer.getChannelData(0);
        const samples = 200; // Number of waveform bars
        const blockSize = Math.floor(channelData.length / samples);
        const waveform = [];

        for (let i = 0; i < samples; i++) {
          let sum = 0;
          for (let j = 0; j < blockSize; j++) {
            sum += Math.abs(channelData[i * blockSize + j]);
          }
          waveform.push(sum / blockSize);
        }

        // Normalize to 0-1 range
        const max = Math.max(...waveform);
        const normalizedWaveform = max > 0 ? waveform.map(val => val / max) : waveform;

        console.log('🔧 Waveform generated, samples:', normalizedWaveform.length, 'max amplitude:', max);

        setWaveformData(normalizedWaveform);
        setDuration(audioBuffer.duration);
        setIsLoading(false);
      } catch (error) {
        console.error('❌ Error generating waveform:', error);

        // Fallback to fake waveform data for testing
        console.log('🔧 Generating fake waveform data as fallback');
        const fakeWaveform = Array.from({ length: 200 }, () => Math.random() * 0.8 + 0.1);
        setWaveformData(fakeWaveform);
        setDuration(30); // Fake 30 second duration
        setIsLoading(false);
      }
    };

    generateWaveform();
  }, [src]);

    // Draw waveform on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !waveformData.length) {
      console.log('🔧 Canvas or waveform data not ready:', { canvas: !!canvas, waveformLength: waveformData.length });
      return;
    }

    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;

    console.log('🎨 Drawing waveform:', { width, height, waveformLength: waveformData.length, currentTime, duration });

    // Clear canvas completely first
    ctx.clearRect(0, 0, width, height);

    // Then fill with dark background
    ctx.fillStyle = 'hsla(230, 15%, 12%, 1)'; // Opaque dark background
    ctx.fillRect(0, 0, width, height);

    const barWidth = width / waveformData.length;
    const progress = duration > 0 ? currentTime / duration : 0;
    const progressX = progress * width;

    console.log('🎨 Progress:', { progress: progress.toFixed(3), progressX: progressX.toFixed(1) });

    waveformData.forEach((amplitude, index) => {
      const barHeight = Math.max(amplitude * height * 0.8, 2); // Minimum height of 2px
      const x = index * barWidth;
      const y = (height - barHeight) / 2;

      // Color based on progress - adjusted for dark background
      if (x < progressX) {
        ctx.fillStyle = 'hsl(166, 69%, 71%)'; // Played portion (bright teal)
      } else {
        ctx.fillStyle = 'hsla(0, 0%, 60%, 0.4)'; // Unplayed portion (subtle gray for dark background)
      }

      ctx.fillRect(x, y, Math.max(barWidth - 1, 1), barHeight);
    });

    // Draw progress line
    if (progress > 0 && progressX > 0) {
      ctx.strokeStyle = 'hsl(166, 69%, 81%)'; // Bright teal progress line
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(progressX, 0);
      ctx.lineTo(progressX, height);
      ctx.stroke();
      console.log('🎨 Drew progress line at:', progressX);
    }

    // Draw hover line
    if (hoverTime >= 0) {
      const hoverProgress = duration > 0 ? hoverTime / duration : 0;
      const hoverX = hoverProgress * width;
      ctx.strokeStyle = 'hsla(166, 69%, 81%, 0.5)'; // Teal hover line with transparency
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(hoverX, 0);
      ctx.lineTo(hoverX, height);
      ctx.stroke();
      ctx.setLineDash([]); // Reset line dash
    }
  }, [waveformData, currentTime, duration, hoverTime]);

  // Handle audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      const newTime = audio.currentTime;
      setCurrentTime(newTime);
      console.log('⏰ Time update:', { currentTime: newTime.toFixed(2), duration: audio.duration?.toFixed(2) });
      if (onTimeUpdate) onTimeUpdate();
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setCurrentTime(0);
      if (onEnded) onEnded();
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [onTimeUpdate, onEnded]);

        const handlePlayPause = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (disabled) return;

    console.log('🎵 WaveformPlayer Play/Pause clicked, isCurrentlyPlaying:', isCurrentlyPlaying);

    if (isCurrentlyPlaying) {
      // This audio is currently playing, so pause it
      console.log('🎵 Pausing audio via button');
      audioRef.current.pause();
      if (onPause) onPause();
    } else {
      // This audio is not playing, so start it
      console.log('🎵 Starting audio via button');
      audioRef.current.currentTime = currentTime; // Resume from current position
      audioRef.current.play().catch(e => console.error('Play failed:', e));
      if (onPlay) onPlay();
    }
  };

    const handleCanvasClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled || !audioRef.current || !duration) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    // Use the actual canvas internal dimensions for precise calculation
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const displayWidth = rect.width;
    const displayHeight = rect.height;

    // Calculate precise position accounting for canvas scaling
    const x = (e.clientX - rect.left) * (canvasWidth / displayWidth);
    const y = (e.clientY - rect.top) * (canvasHeight / displayHeight);

    // Calculate progress with higher precision
    const progress = Math.max(0, Math.min(1, x / canvasWidth));
    const newTime = progress * duration;

    console.log('🎯 Canvas clicked:', {
      clickX: (e.clientX - rect.left).toFixed(1),
      canvasX: x.toFixed(1),
      progress: progress.toFixed(4),
      newTime: newTime.toFixed(3),
      duration: duration.toFixed(3)
    });

    // Smooth seeking with visual feedback
    const wasPlaying = !audioRef.current.paused;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);

    // If it was playing, continue playing after seek
    if (wasPlaying) {
      audioRef.current.play().catch(e => console.error('Resume after seek failed:', e));
         }
  };

  const handleCanvasMouseMove = (e) => {
    if (disabled || !duration) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const canvasWidth = canvas.width;
    const displayWidth = rect.width;

    const x = (e.clientX - rect.left) * (canvasWidth / displayWidth);
    const progress = Math.max(0, Math.min(1, x / canvasWidth));
    const timeAtHover = progress * duration;

    setHoverTime(timeAtHover);
  };

  const handleCanvasMouseLeave = () => {
    setHoverTime(-1);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`waveform-player ${className} ${disabled ? 'disabled' : ''}`}>
      <audio ref={audioRef} src={src} loop={loop} />

      <div className="waveform-container">
                <button
          className={`play-button ${isCurrentlyPlaying ? 'playing' : ''} ${isLoading ? 'loading' : ''}`}
          onClick={(e) => handlePlayPause(e)}
          disabled={disabled || isLoading}
        >
          <div className="play-icon" />
          <div className="pause-icon" />
        </button>

                <div className="waveform-display">
          {isLoading ? (
            <div className="waveform-loading">Loading waveform...</div>
          ) : waveformData.length > 0 ? (
                        <canvas
              ref={canvasRef}
              width={800}
              height={160}
              onClick={handleCanvasClick}
              onMouseMove={handleCanvasMouseMove}
              onMouseLeave={handleCanvasMouseLeave}
              style={{
                cursor: disabled ? 'default' : 'pointer',
                width: '100%',
                height: '80px'
              }}
            />
          ) : (
            <div className="waveform-error">
              <div>Waveform unavailable</div>
              <div style={{ fontSize: '12px', color: 'hsl(0, 0%, 65%)' }}>
                Playing audio: {currentTime.toFixed(1)}s / {duration.toFixed(1)}s
              </div>
            </div>
          )}
        </div>

        <div className="time-display">
          {hoverTime >= 0 ? (
            <div className="hover-time">→ {formatTime(hoverTime)}</div>
          ) : (
            formatTime(currentTime)
          )}
        </div>
      </div>
    </div>
  );
});

WaveformPlayer.displayName = 'WaveformPlayer';

export default WaveformPlayer;
